import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { CheckoutPaymentMethodsAgreement } from 'src/app/+checkout-purchase/components/checkout-payment-methods.component/interfaces';
import { AuthenticatedBsPaymentRequest } from 'src/app/model/api/api-request/autenticated-bs-payment-request';
import { ChargeRecurringRequest } from 'src/app/model/api/api-request/billwerk/charge-recurring-request';
import { ChargeRequest } from 'src/app/model/api/api-request/billwerk/charge-request';
import { ChargeSettleRequest } from 'src/app/model/api/api-request/billwerk/charge-settle-request';
import { CustomerPaymentMethodActiveRequest } from 'src/app/model/api/api-request/billwerk/customer-payment-methods-active-request';
import { CustomerPaymentMethodsAgreementsRequest } from 'src/app/model/api/api-request/billwerk/customer-payment-methods-agreements-request';
import { CustomerRequest } from 'src/app/model/api/api-request/billwerk/customer-request';
import { PaymentMethodDeleteRequest } from 'src/app/model/api/api-request/billwerk/payment-method-delete-request';
import { PaymentMethodGetRequest } from 'src/app/model/api/api-request/billwerk/payment-method-get-request';
import { PaymentMethodPostRequest } from 'src/app/model/api/api-request/billwerk/payment-method-post-request';
import { PayMethodsRequest } from 'src/app/model/api/api-request/pay-methods-request';
import { PaymentBsRequest } from 'src/app/model/api/api-request/payment-bs-request';
import { RecurringBsPaymentRequest } from 'src/app/model/api/api-request/recurring-bs-payment-request';
import { RecurringVippsPaymentRequest } from 'src/app/model/api/api-request/recurring-vipps-payment-request';
import { SCAPaymentEventRequest } from 'src/app/model/api/api-request/sca-payment-event-request';
import { SCAPaymentEvent } from 'src/app/model/enums/sca-payment-event';
import { BankAccount } from 'src/app/model/interfaces/bank-account';
import { BillwerkAddPaymentMethodRequest } from 'src/app/model/interfaces/billwerk-add-payment-method-request';
import { BillwerkAddPaymentMethodResponse } from 'src/app/model/interfaces/billwerk-add-payment-method-response';
import { BillwerkChargeRecurringRequest, BillwerkChargeRequest } from 'src/app/model/interfaces/billwerk-charge-recurring';
import { BillwerkCustomer } from 'src/app/model/interfaces/billwerk-customer';
import { BillwerkPaymentMethod } from 'src/app/model/interfaces/billwerk-payment-method';
import { ChargeSettle } from 'src/app/model/interfaces/charge-settle';
import { Order } from 'src/app/model/interfaces/order';
import { PayMethod } from 'src/app/model/interfaces/pay-method';
import { PaymentResponse } from 'src/app/model/interfaces/payment-response';
import { Visitor } from 'src/app/model/visitor';
import { AppConfigService } from 'src/app/services/app-config.service';
import { ApiClient } from '../api-client/api-client';

@Injectable()
export class PaymentRepository
{
    private readonly apiClient: ApiClient;

    public constructor(apiClient: ApiClient, private environment: AppConfigService)
    {
        this.apiClient = apiClient;
    }

    public executeSaleBs(
        visitor: Visitor,
        order: Order,
        bankAccount: BankAccount): Promise<PaymentResponse>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new PaymentBsRequest(
            visitor,
            order,
            bankAccount,
            webapiBasepath
        );

       return firstValueFrom(
        this.apiClient.put<PaymentResponse>(request)
      );
    }

    public executeAuthenticatedBsSale(
        orderId: number,
        bankAccount: BankAccount
    ): Promise<PaymentResponse> {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new AuthenticatedBsPaymentRequest(
            orderId,
            bankAccount,
            webapiBasepath
        );

        return firstValueFrom(
          this.apiClient.put<PaymentResponse>(request)
        );
    }

    public executeRecurringVippsSale(
        orderId: number,
        vippsId: number
    ): Promise<PaymentResponse>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new RecurringVippsPaymentRequest(
            orderId,
            vippsId,
            webapiBasepath
        );

        return firstValueFrom(
          this.apiClient.put<PaymentResponse>(request)
      );
    }

    public executeRecurringBsSale(
        orderId: number,
        bsId: number
    ): Promise<PaymentResponse>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new RecurringBsPaymentRequest(
            orderId,
            bsId,
            webapiBasepath
        );

        return firstValueFrom(
          this.apiClient.put<PaymentResponse>(request)
      );
    }

    public getPayMethods(): Observable<PayMethod[]>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new PayMethodsRequest(webapiBasepath);

        return this.apiClient.get(request);
    }

    public sendSCAPaymentEvent(orderId: number, event: SCAPaymentEvent): Observable<void>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new SCAPaymentEventRequest(orderId, event, webapiBasepath);
        return this.apiClient.post(request);
    }

    //////////////////////////////////////
    ////////////// BILLWERK //////////////
    //////////////////////////////////////

    public chargePayment(payload: BillwerkChargeRequest): Observable<HttpStatusCode>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new ChargeRequest(payload, webapiBasepath);

        return this.apiClient.post(request);
    }

    public chargeAndSettlePayment(payload: ChargeSettle): Observable<HttpStatusCode>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new ChargeSettleRequest(payload, webapiBasepath);

        return this.apiClient.post(request);
    }

    public chargeRecurringPayment(chargeRecurringRequest: BillwerkChargeRecurringRequest): Observable<BillwerkAddPaymentMethodResponse>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new ChargeRecurringRequest(chargeRecurringRequest, webapiBasepath);

        return this.apiClient.post(request);
    }

    public getCustomer(playerId: number): Observable<BillwerkCustomer>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new CustomerRequest(playerId, webapiBasepath);

        return this.apiClient.get(request);
    }

    public getCustomerPaymentMethods(playerId: number): Observable<BillwerkPaymentMethod[]>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new CustomerPaymentMethodActiveRequest(playerId, webapiBasepath);

        return this.apiClient.get(request);
    }

    public getActivePaymentMethodsAgreements(): Observable<CheckoutPaymentMethodsAgreement[]>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new CustomerPaymentMethodsAgreementsRequest(webapiBasepath);

        return this.apiClient.get(request);
    }

    public getActivePaymentMethods(playerId: number): Observable<BillwerkPaymentMethod[]>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new PaymentMethodGetRequest(playerId, webapiBasepath);

        return this.apiClient.get(request);
    }

    public addPaymentMethod(paymentMethodDetails: BillwerkAddPaymentMethodRequest): Observable<BillwerkAddPaymentMethodResponse>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new PaymentMethodPostRequest(paymentMethodDetails, webapiBasepath);

        return this.apiClient.post(request);
    }

    public removePaymentMethod(paymentMethodId: string): Observable<BillwerkPaymentMethod>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new PaymentMethodDeleteRequest(paymentMethodId, webapiBasepath);

        return this.apiClient.delete(request);
    }
}
